/**
 * Add Device Modal - Simplified UUID-based pairing
 * Allows family members to pair a new device using a pairing code
 */

import React, { useState } from 'react';
import Modal from './ui/hayat/Modal';
import { pairDevice } from '../api/deviceApi';

function AddDeviceModal({ isOpen, onClose, seniors, onSuccess }) {
  const [step, setStep] = useState(1);
  const [pairingCode, setPairingCode] = useState('');
  const [selectedSenior, setSelectedSenior] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [error, setError] = useState(null);
  const [pairedDevice, setPairedDevice] = useState(null);

  const handleClose = () => {
    if (!isPairing) {
      setStep(1);
      setPairingCode('');
      setSelectedSenior('');
      setDeviceName('');
      setError(null);
      setPairedDevice(null);
      onClose();
    }
  };

  const handlePair = async () => {
    if (!pairingCode.trim()) {
      setError('Bitte geben Sie den Kopplungs-Code ein.');
      return;
    }

    // Validate pairing code format: Must be exactly 6 digits
    const cleaned = pairingCode.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      setError('Bitte geben Sie einen gültigen 6-stelligen Code ein (Format 123-456).');
      return;
    }

    if (!selectedSenior) {
      setError('Bitte wählen Sie einen Senior aus.');
      return;
    }

    setError(null);
    setIsPairing(true);

    try {
      // Pass raw code - deviceApi.js handles normalization to XXX-XXX format
      const device = await pairDevice({
        pairingCode: pairingCode.trim(),
        seniorId: selectedSenior,
        deviceName: deviceName || null
      });

      // Success
      setPairedDevice(device);
      setStep(2);

      if (onSuccess) {
        onSuccess(device);
      }
    } catch (err) {
      // Error already logged in deviceApi.js
      setError(err.message || 'Fehler beim Koppeln des Geräts.');
    } finally {
      setIsPairing(false);
    }
  };

  const getSeniorName = (seniorId) => {
    const senior = seniors?.find(s => s.id === seniorId);
    return senior?.name || 'Unbekannt';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 1 ? 'Gerät hinzufügen' : 'Gerät erfolgreich gekoppelt!'}
      size="md"
    >
      {step === 1 && (
        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-200 mb-2">So funktioniert's:</h4>
                <ol className="text-sm text-blue-300 space-y-1 list-decimal list-inside">
                  <li>Öffnen Sie die Hayat-App auf dem Gerät</li>
                  <li>Tippen Sie auf "Gerät koppeln"</li>
                  <li>Notieren Sie den angezeigten 6-stelligen Code</li>
                  <li>Geben Sie den Code hier ein</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Pairing Code Input */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Kopplungs-Code *
            </label>
            <input
              type="text"
              value={pairingCode}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                if (value.length <= 6) {
                  setPairingCode(value);
                }
              }}
              placeholder="ABC123"
              maxLength={6}
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-center text-2xl font-mono tracking-widest placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isPairing}
              autoFocus
            />
            <p className="text-xs text-neutral-500 mt-1 text-center">
              6-stelliger Code vom Gerät
            </p>
          </div>

          {/* Senior Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Für wen ist das Gerät? *
            </label>
            <select
              value={selectedSenior}
              onChange={(e) => setSelectedSenior(e.target.value)}
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isPairing}
            >
              <option value="">-- Bitte wählen --</option>
              {seniors?.map((senior) => (
                <option key={senior.id} value={senior.id}>
                  {senior.name}
                </option>
              ))}
            </select>
          </div>

          {/* Device Name (Optional) */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Gerätename (optional)
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="z.B. Tablet Wohnzimmer"
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isPairing}
            />
            <p className="text-xs text-neutral-500 mt-1">
              Leer lassen für automatische Benennung
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-200 px-4 py-3 rounded-lg flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <p className="text-sm flex-1">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isPairing}
              className="flex-1 px-6 py-3 bg-neutral-700 text-white rounded-lg hover:bg-neutral-600 transition-colors disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handlePair}
              disabled={isPairing || !pairingCode.trim() || !selectedSenior}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isPairing ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Wird gekoppelt...</span>
                </>
              ) : (
                'Gerät koppeln'
              )}
            </button>
          </div>
        </div>
      )}

      {step === 2 && pairedDevice && (
        <div className="space-y-6">
          {/* Success Message */}
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">✅</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Gerät erfolgreich gekoppelt!
            </h3>
            <p className="text-neutral-400">
              Das Gerät wurde mit {getSeniorName(selectedSenior)} verbunden.
            </p>
          </div>

          {/* Device Info */}
          <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-neutral-400 text-sm">Gerätename:</span>
              <span className="text-white font-medium">
                {pairedDevice.device_name || pairedDevice.name || 'Unbenannt'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400 text-sm">Zugeordnet zu:</span>
              <span className="text-white font-medium">{getSeniorName(selectedSenior)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400 text-sm">Status:</span>
              <span className="text-green-400 font-medium">Gekoppelt</span>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Fertig
          </button>
        </div>
      )}
    </Modal>
  );
}

export default AddDeviceModal;
